// 尾随合并的 zustand persist 存储（web / localStorage）。
//
// why：persist 中间件在**每次** setState 后无条件把 partialized 快照写 storage——j/k 移动、
// 多选、开抽屉这类纯 UI 动作也会触发整包 JSON.stringify（2-3k task ≈ 0.5-1MB）+ 同步
// localStorage 写，全在主线程。批量 action（逐条 set()）还会 K 倍放大。
//
// how：实现自定义 PersistStorage（而非 createJSONStorage）——setItem 收到的是**未序列化**的
// StorageValue 对象（state 是不可变快照，持引用安全）。只保留最后一次快照，trailing DELAY 后
// 才序列化+落盘（首写后至多 DELAY 毫秒，节流带尾随）。pagehide/beforeunload/隐藏时同步 flush，
// 崩溃丢失窗口 ≤DELAY——同步意图本就单独即时持久化在 outbox，不受影响。
import type { PersistStorage, StorageValue } from 'zustand/middleware';

const DELAY_MS = 150;

// 代际守卫：vitest 的 vi.resetModules 会让**旧**模块实例的尾随定时器在新模块已重建 store 后
// 才触发，把旧快照写回 localStorage 污染下一个用例。每次工厂调用（=模块重载）代际 +1，
// 旧代际的 flush 直接跳过。生产运行时工厂只调一次，无行为影响。
const GEN_KEY = '__doneline_persist_gen__';

// 最近一次落盘是否失败（QuotaExceeded / 隐私模式禁写）。模块级：importPayload 等大写入
// 路径在 flushPersistWrites() 之后查询它，把「静默丢数据」变成显式错误提示。任一次
// 成功写入即清（下次快照会带上全量 state，重试成功 = 数据已完整落盘）。
let writeFailed = false;

/** 最近一次 persist 落盘是否失败（true = 内存 state 落后于盘上，刷新即丢增量）。 */
export function hasPersistWriteFailure(): boolean {
  return writeFailed;
}

// 读降级写保护（设计 v4 §8）：getItem 解析失败 = 盘上有读不出的快照。此时内存是
// 默认空 state，任何覆盖写都会把盘上完好数据抹掉 —— 对 authed 可由 bootstrap 恢复，
// 对 guest 是**唯一副本的永久丢失**。dirtyDisk 生效期间拒绝覆盖写（宁丢增量不丢全量），
// 账号边界写路径（clearLocalData / discard / 换号 wipe）用 allowPersistOverwrite 强制
// 覆盖并复位（outbox.clear 绕 RMW 的同款先例）。
let dirtyDisk = false;
let overwriteAllowed = false;

export function hasPersistDirtyDisk(): boolean {
  return dirtyDisk;
}

/** 账号边界写（clear/discard/wipe）：目标态就是覆盖，放行下一轮 flush 并复位 dirtyDisk。 */
export function allowPersistOverwrite(): void {
  overwriteAllowed = true;
}

// 每个工厂实例把自己的 flushAll 注册进来，供 flushPersistWrites() 立即冲刷（代际守卫
// 让过期实例的 flush 自然 no-op，不用反注册）。
const flushers = new Set<() => void>();

/** 立刻冲刷全部 pending 快照（大写入后同步检查失败标记前调用；平时靠尾随定时器）。 */
export function flushPersistWrites(): void {
  for (const f of [...flushers]) f();
}

export interface CoalescedStorageOpts<S> {
  /**
   * 写侧 read-modify-write 钩子（跨窗口对称性，guardrail A「共享 localStorage 必须
   * RMW」的 store 侧）：flush 时若发现盘上值不是本窗口最后写入的（= 另一窗口在
   * 尾随窗口内 flush 过），先把盘上快照并进待写快照再落盘，绝不整包覆盖对方的写。
   * 单窗口下盘上值恒等于 lastWritten，钩子零调用零解析开销。
   */
  mergeOnForeignWrite?: (pending: StorageValue<S>, disk: StorageValue<S>) => StorageValue<S>;
}

export function coalescedLocalStorage<S>(
  delayMs = DELAY_MS,
  opts?: CoalescedStorageOpts<S>,
): PersistStorage<S> {
  const g = globalThis as Record<string, unknown>;
  const gen = ((g[GEN_KEY] as number | undefined) ?? 0) + 1;
  g[GEN_KEY] = gen;
  const isCurrent = () => (g[GEN_KEY] as number) === gen;

  const pending = new Map<string, StorageValue<S>>();
  const timers = new Map<string, ReturnType<typeof setTimeout>>();
  // 本窗口最后一次成功写入的序列化串（按 key）。盘上值 ≠ 它 ⇒ 有「外来写」需要 RMW 合并。
  const lastWritten = new Map<string, string>();
  // 最后一次「已排队或已落盘」的 partialized state（按 key）。UI-only setState（j/k、
  // 多选、开抽屉——白名单键引用全等）直接短路，连 pending 都不置（perf B2：这些写
  // 100% 是浪费——UI 字段不在 partialize 白名单里，落盘内容不会变）。
  // 失败路径必须清掉它：flush 失败后若继续短路，重试永远不会发生。
  const lastQueuedState = new Map<string, S>();

  // partialize 快照是每次新建的包装对象，但白名单键的值是不可变引用——逐键引用比较即可。
  const sameSnapshot = (a: S, b: S): boolean => {
    if (a === b) return true;
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
    const ka = Object.keys(a as object);
    const kb = Object.keys(b as object);
    if (ka.length !== kb.length) return false;
    for (const k of ka) {
      if ((a as Record<string, unknown>)[k] !== (b as Record<string, unknown>)[k]) return false;
    }
    return true;
  };

  const flushKey = (name: string) => {
    if (!isCurrent()) return; // 过期模块实例：放弃写入
    const v = pending.get(name);
    if (v === undefined) return;
    pending.delete(name);
    const t = timers.get(name);
    if (t) {
      clearTimeout(t);
      timers.delete(name);
    }
    let out = v;
    if (opts?.mergeOnForeignWrite) {
      try {
        const raw = localStorage.getItem(name);
        // raw === null（首写/被清）或 === lastWritten（无外来写）都直接写；只有真正的
        // 外来写才付一次 parse + merge 的代价（仅双窗口并发编辑时发生）。
        if (raw !== null && raw !== lastWritten.get(name)) {
          out = opts.mergeOnForeignWrite(v, JSON.parse(raw) as StorageValue<S>);
        }
      } catch {
        out = v; // 盘上值不可解析 → 按待写快照落盘（与旧行为一致）
      }
    }
    // 读降级期间拒写（v4 §8）：盘上有读不出的快照，覆盖 = 永久丢失（guest 无服务器副本）。
    // 标记 writeFailed 让 import 等路径把「静默丢数据」变成显式错误。
    if (dirtyDisk && !overwriteAllowed) {
      writeFailed = true;
      lastQueuedState.delete(name); // 拒写 ⇒ 下一次同内容 setItem 必须重新排队（不得被短路吞掉）
      console.error('[persist] write refused: disk snapshot unreadable (dirtyDisk)');
      return;
    }
    try {
      const serialized = JSON.stringify(out);
      localStorage.setItem(name, serialized);
      lastWritten.set(name, serialized);
      writeFailed = false;
      dirtyDisk = false;
      overwriteAllowed = false;
    } catch (err) {
      writeFailed = true;
      lastQueuedState.delete(name); // 同上：失败后短路会让重试永不发生
      console.error('[persist] write failed', err);
    }
  };
  const flushAll = () => {
    for (const name of [...pending.keys()]) flushKey(name);
  };
  flushers.add(flushAll);

  if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    // 刷新/关闭/切后台前落盘（pagehide 覆盖导航与刷新；visibilitychange 覆盖桌面壳隐藏窗口）。
    window.addEventListener('pagehide', flushAll);
    window.addEventListener('beforeunload', flushAll);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flushAll();
    });
  }

  return {
    getItem: (name) => {
      // 先吐 pending（read-your-writes：rehydrate 不能读到落后于内存的旧盘上值）。
      const p = pending.get(name);
      if (p !== undefined) return p;
      try {
        const raw = localStorage.getItem(name);
        return raw ? (JSON.parse(raw) as StorageValue<S>) : null;
      } catch {
        // 盘上有值但读不出（JSON 损坏 / 禁读）→ 置 dirtyDisk，写侧拒绝覆盖（v4 §8）。
        dirtyDisk = true;
        console.error('[persist] read failed — dirtyDisk set, overwrites refused');
        return null;
      }
    },
    setItem: (name, value) => {
      // UI-only setState 短路（perf B2）：白名单键引用全等 ⇒ 落盘内容不变。
      // 账号边界写（allowPersistOverwrite / dirtyDisk 恢复）绕过——目标态就是强制覆盖。
      const prev = lastQueuedState.get(name);
      if (
        prev !== undefined &&
        !overwriteAllowed &&
        !dirtyDisk &&
        sameSnapshot(value.state, prev)
      ) {
        return;
      }
      lastQueuedState.set(name, value.state);
      pending.set(name, value);
      if (!timers.has(name)) {
        timers.set(
          name,
          setTimeout(() => {
            timers.delete(name);
            flushKey(name);
          }, delayMs),
        );
      }
    },
    removeItem: (name) => {
      pending.delete(name);
      lastWritten.delete(name);
      lastQueuedState.delete(name);
      const t = timers.get(name);
      if (t) {
        clearTimeout(t);
        timers.delete(name);
      }
      try {
        localStorage.removeItem(name);
      } catch {
        // ignore
      }
    },
  };
}
